import React, { type HTMLAttributes } from 'react';
import styled, { css } from 'styled-components';
import { Spacing12, Spacing16, Spacing24, Spacing32, Spacing48, Spacing64 } from 'autogen/design-tokens/tokens';
import type { ExtendedPlaceholderModel } from 'autogen/swagger/all';
import ArticleContentRow from 'components/pages/placeholders/ExtendedPlaceholder/ArticleContentRow';
import ExtendedPlaceholderHeader from 'components/pages/placeholders/ExtendedPlaceholder/ExtendedPlaceholderHeader';
import ProductRow from 'components/pages/placeholders/ExtendedPlaceholder/ProductRow';
import ShopByRow from 'components/pages/placeholders/ExtendedPlaceholder/ShopByRow';
import ThemeProvider from 'theming/ThemeProvider';
import { breakpoint, sizes } from 'variables';
import InventoryLane from './InventoryLane';

const defaultTheme = css`
    --background-color: ${({ theme }) => theme.ColorBackgroundSecondary};
`;

const transparentTheme = css`
    --background-color: transparent;
`;

const plpTheme = css`
    --background-color: ${({ theme }) => theme.ColorBackgroundSecondary};
`;

const Wrapper = styled.div<
    Pick<ExtendedPlaceholderModel, 'transparentBackground' | 'useProductListBackgroundColor'> & {
        addMargin: boolean;
        addHorizontalPadding: boolean;
    }
>`
    ${({ transparentBackground, useProductListBackgroundColor }) => {
        if (transparentBackground) {
            return transparentTheme;
        }

        if (useProductListBackgroundColor) {
            return plpTheme;
        }

        return defaultTheme;
    }}

    ${({ addMargin }) =>
        addMargin &&
        css`
            margin-bottom: ${Spacing32};

            ${breakpoint.up(sizes.lg)} {
                margin-bottom: ${Spacing48};
            }
        `}

    ${({ addHorizontalPadding }) =>
        addHorizontalPadding &&
        css`
            padding: 0 ${Spacing16};
        `}
`;

const Background = styled.div<{
    shouldFillHeaderBackground: boolean;
}>`
    background: var(--background-color);
    position: relative;

    margin: 0 -${Spacing16};

    ${({ shouldFillHeaderBackground }) =>
        shouldFillHeaderBackground &&
        css`
            &:before {
                content: '';
                position: absolute;
                z-index: -1;
                height: calc(${Spacing12} + ${Spacing24});
                background: var(--background-color);
                bottom: 100%;
                width: 100%;
            }

            ${breakpoint.up(sizes.lg)} {
                &:before {
                    height: calc(${Spacing24} + ${Spacing24});
                }
            }
        `}

    &:after {
        content: '';
        height: 100%;
        background: var(--background-color);
        position: absolute;
        top: 0;
        z-index: -1;
        width: 100%;

        ${breakpoint.up(sizes.md)} {
            height: 100%;
        }
    }
`;

const ContentContainer = styled.div<{
    removeBodySpacing: boolean;
    hasHeader: boolean;
}>`
    max-width: 100%;
    margin: 0 auto;
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    grid-gap: ${Spacing32};

    ${({ removeBodySpacing, hasHeader }) =>
        removeBodySpacing
            ? css`
                  padding-top: ${hasHeader ? Spacing32 : 0};
                  padding-left: ${Spacing16};
                  padding-right: ${Spacing16};
                  padding-bottom: 0;

                  ${breakpoint.up(sizes.md)} {
                      padding-top: ${hasHeader ? Spacing64 : 0};
                  }
              `
            : css`
                  padding: ${Spacing32} ${Spacing16};
                  ${breakpoint.up(sizes.md)} {
                      padding: ${Spacing64} ${Spacing16};
                  }
              `}

    ${breakpoint.up(sizes.xl)} {
        max-width: 1230px;
        grid-gap: ${Spacing64};
    }

    ${breakpoint.up(sizes.xxl)} {
        max-width: 1400px;
    }

    ${breakpoint.up(sizes.xxxl)} {
        max-width: 1680px;
    }
`;

export interface IExtendedPlaceholderProps {
    model: ExtendedPlaceholderModel;
    shouldDisplayAsH1?: boolean;
    addHorizontalPadding?: boolean;
}

function ExtendedPlaceholder(props: HTMLAttributes<HTMLDivElement> & IExtendedPlaceholderProps): JSX.Element {
    const {
        model: {
            designThemeName,
            transparentBackground,
            headerModel,
            isPreview,
            contentModel,
            shopByModel,
            productsModel,
            inventoryModel,
            contentBlockId,
            removeSpacingBeforeNextElement,
            removeBodySpacing,
            useProductListBackgroundColor,
            plusOfferNudgeModel,
            ...restProps
        },
        shouldDisplayAsH1,
        addHorizontalPadding = false,
        className
    } = props;

    const empty = !headerModel && !contentModel && !shopByModel && !productsModel && !inventoryModel;
    const showPreviewSpace = empty && isPreview;
    const hasSecondaryModules = !!contentModel || !!shopByModel || !!productsModel;

    const shouldFillHeaderBackground = !!headerModel && (headerModel.includeStripes || !!headerModel.image);

    return (
        <ThemeProvider theme={designThemeName}>
            {showPreviewSpace && 'No data, this will not be shown on the website'}
            <Wrapper
                addMargin={!removeSpacingBeforeNextElement && !transparentBackground && !empty && !inventoryModel}
                addHorizontalPadding={addHorizontalPadding}
                useProductListBackgroundColor={useProductListBackgroundColor}
                transparentBackground={transparentBackground}
                className={className}
                {...restProps}
            >
                {headerModel && (
                    <ExtendedPlaceholderHeader
                        shouldDisplayAsH1={!!shouldDisplayAsH1}
                        model={headerModel}
                        contentBlockId={contentBlockId}
                        plusOfferNudgeModel={plusOfferNudgeModel}
                    />
                )}
                <Background shouldFillHeaderBackground={shouldFillHeaderBackground}>
                    {hasSecondaryModules && (
                        <ContentContainer removeBodySpacing={removeBodySpacing} hasHeader={!!headerModel}>
                            {shopByModel && (
                                <ShopByRow model={shopByModel} transparentBackground={transparentBackground} />
                            )}
                            {contentModel && <ArticleContentRow model={contentModel} />}
                            <ThemeProvider theme="default">
                                {productsModel && <ProductRow model={productsModel} />}
                            </ThemeProvider>
                        </ContentContainer>
                    )}
                </Background>
                {inventoryModel &&
                    (inventoryModel.inventoryLane ? (
                        <InventoryLane model={inventoryModel} />
                    ) : (
                        <div>{inventoryModel.heading}</div>
                    ))}
            </Wrapper>
        </ThemeProvider>
    );
}

export default ExtendedPlaceholder;
